/* ============================================
   GOAT COMMUNITY LANDING PAGE — Effects & Interactions
   ============================================ */

(function() {
  'use strict';

  // ===== VOLUME CONTROL =====
  function initVolumeControl() {
    const control = document.getElementById('goatVolume');
    if (!control) return;
    const video = document.getElementById('goatHeroVideo');
    if (!video) return;
    
    const statusEl = control.querySelector('.goat-volume__status');
    const iconOff = control.querySelector('.goat-volume__icon--off');
    const iconOn = control.querySelector('.goat-volume__icon--on');
    
    function applyUI() {
      if (video.muted) {
        control.classList.remove('is-on');
        control.style.background = 'rgba(0,0,0,0.5)';
        control.style.color = '#fff';
        if (statusEl) statusEl.textContent = 'Sound Off';
        if (iconOff) iconOff.style.display = '';
        if (iconOn) iconOn.style.display = 'none';
      } else {
        control.classList.add('is-on');
        control.style.background = '#fff';
        control.style.color = '#000';
        if (statusEl) statusEl.textContent = 'Sound On';
        if (iconOff) iconOff.style.display = 'none';
        if (iconOn) iconOn.style.display = '';
      }
    }
    
    control.setAttribute('role', 'button');
    control.setAttribute('tabindex', '0');
    
    control.addEventListener('click', () => {
      video.muted = !video.muted;
      applyUI();
    });
    
    control.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        video.muted = !video.muted;
        applyUI();
      }
    });
    
    video.addEventListener('loadeddata', applyUI);
  }

  // ===== SCROLL REVEAL TEXT =====
  function initScrollRevealText() {
    const els = document.querySelectorAll('.goat-scroll-text');
    if (els.length === 0) return;
    
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      els.forEach(el => {
        el.style.color = '#f5f3eb';
        el.style.background = 'none';
      });
      return;
    }
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.backgroundImage = 'none';
          entry.target.style.color = '#f5f3eb';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });
    
    els.forEach(el => observer.observe(el));
  }

  // ===== FADE MASK ANIMATION =====
  function initFadeMask() {
    const masks = document.querySelectorAll('.goat-fade-mask');
    if (masks.length === 0) return;
    
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReduced) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'goatFadeMaskIn 1.2s var(--goat-ease) forwards';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    
    masks.forEach(el => observer.observe(el));
  }

  // ===== ARTIST SLIDER (No Swiper dependency — pure JS drag scroll) =====
  function initArtistSlider() {
    const containers = document.querySelectorAll('.goat-slider');
    if (containers.length === 0) return;
    
    containers.forEach(container => {
      const track = container.querySelector('.goat-slider__track');
      if (!track) return;
      
      const btnPrev = container.querySelector('.goat-slider__btn--prev');
      const btnNext = container.querySelector('.goat-slider__btn--next');
      
      let isDown = false;
      let startX;
      let scrollLeft;
      
      track.addEventListener('mousedown', (e) => {
        isDown = true;
        track.style.cursor = 'grabbing';
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft || track.scrollLeft;
      });
      
      track.addEventListener('mouseleave', () => { isDown = false; track.style.cursor = 'grab'; });
      track.addEventListener('mouseup', () => { isDown = false; track.style.cursor = 'grab'; });
      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        const walk = (x - startX) * 1.5;
        if (track.scrollLeft !== undefined) {
          track.scrollLeft = scrollLeft - walk;
        }
      });
      
      // Touch support
      track.addEventListener('touchstart', (e) => {
        isDown = true;
        startX = e.touches[0].pageX - track.offsetLeft;
      }, { passive: true });
      
      track.addEventListener('touchend', () => { isDown = false; });
      
      track.addEventListener('touchmove', (e) => {
        if (!isDown) return;
        const x = e.touches[0].pageX - track.offsetLeft;
        const walk = (x - startX) * 1.5;
        track.parentElement.scrollLeft = (track.parentElement.scrollLeft || 0) - walk;
      }, { passive: true });
      
      function slide(direction) {
        const scrollAmount = 304; // card width + gap
        const target = (track.parentElement.scrollLeft || 0) + (direction * scrollAmount);
        track.parentElement.scrollTo({ left: target, behavior: 'smooth' });
      }
      
      if (btnPrev) btnPrev.addEventListener('click', () => slide(-1));
      if (btnNext) btnNext.addEventListener('click', () => slide(1));
    });
  }

  // ===== DAY→NIGHT TRANSFORMATION =====
  function initDayNight() {
    const section = document.getElementById('goatDayNight');
    if (!section) return;
    
    const bg = section.querySelector('.goat-daynight__bg');
    const fill = section.querySelector('.goat-daynight__fill');
    const stars = section.querySelector('.goat-daynight__stars');
    const fromEl = document.getElementById('goatDayFrom');
    const toEl = document.getElementById('goatDayTo');
    
    if (!bg || !fill) return;
    
    let ticking = false;
    
    function updateDayNight() {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const viewportHeight = window.innerHeight;
      
      // Calculate progress: 0 = section entering viewport from bottom, 1 = section leaving at top
      const progress = Math.max(0, Math.min(1, 1 - (sectionTop + sectionHeight * 0.5) / viewportHeight));
      
      fill.style.width = (progress * 100) + '%';
      
      // Background transitions
      if (progress < 0.33) {
        bg.className = 'goat-daynight__bg goat-daynight__bg--day';
        stars.classList.remove('visible');
        if (fromEl) { fromEl.style.opacity = '1'; toEl.style.opacity = '0'; }
      } else if (progress < 0.66) {
        bg.className = 'goat-daynight__bg goat-daynight__bg--dusk';
        stars.classList.remove('visible');
        if (fromEl) { fromEl.style.opacity = '1'; toEl.style.opacity = '0'; }
      } else {
        bg.className = 'goat-daynight__bg goat-daynight__bg--night';
        stars.classList.add('visible');
        if (fromEl) { fromEl.style.opacity = '0'; toEl.style.opacity = '1'; }
      }
      
      ticking = false;
    }
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateDayNight);
        ticking = true;
      }
    }, { passive: true });
    
    // Initial call
    updateDayNight();
  }

  // ===== FLOATING ELEMENTS PARALLAX =====
  function initFloatingParallax() {
    const floating = document.querySelectorAll('.goat-float');
    if (floating.length === 0) return;
    
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    
    let ticking = false;
    
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          floating.forEach((el, i) => {
            const speed = 0.02 + (i * 0.005);
            const y = window.scrollY * speed * (i % 2 === 0 ? 1 : -1);
            el.style.transform = `translateY(${y}px)`;
          });
          ticking = false;
        });
        ticking = true;
      }
    }
    
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ===== INITIALIZE ALL =====
  document.addEventListener('DOMContentLoaded', () => {
    initVolumeControl();
    initScrollRevealText();
    initFadeMask();
    initArtistSlider();
    initDayNight();
    initFloatingParallax();
  });

})();
